import { Fragment, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiDelete, apiDownload, ApiError } from '../api/client';
import { businessBySlug } from '../domain/businesses';
import { useAuth, ROLES } from '../auth/AuthContext';
import { formatMkd, formatDate, formatNumber, todayIso, monthAgoIso } from '../lib/format';
import FilterBar, { btn } from '../components/FilterBar';
import FitimModal from '../components/FitimModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Fitimet for one business. Ports Fitimet{Stargym,Starbar,Missfit}.
 *
 * Rows are grouped by date with a subtotal after each day, matching the green
 * "Total" rows the desktop grid inserted while streaming.
 *
 * Stargroup has no revenue rows of its own — it redirects to the comparison
 * view instead.
 */

interface FitimRow {
  ID: number;
  data: string;
  artikulli: string | null;
  cmimi: number;
  numri: number | null;
  periudha: string | null;
  hasFile: number;
}

interface Response {
  rows: FitimRow[];
  total: number;
  dayTotals: { data: string; total: number }[];
}

interface HeaderTotals {
  businesses: { slug: string; name: string; total: number }[];
  grandTotal: number;
}

export default function Fitimet() {
  const { biznesi = '' } = useParams();
  const business = businessBySlug(biznesi);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [deleting, setDeleting] = useState<FitimRow | null>(null);

  useEffect(() => setSearch(''), [biznesi]);

  const hasRevenue = business && business.slug !== 'stargroup';

  const listQuery = useQuery({
    queryKey: ['fitimet', biznesi, search, startDate, endDate],
    queryFn: () =>
      apiGet<Response>(`/fitimet/${biznesi}/rreshtat`, { search, startDate, endDate }),
    enabled: Boolean(hasRevenue),
    placeholderData: (prev) => prev,
  });

  // The strip of current-month figures frmFitimet keeps above these screens
  // (frmFitimet.cs:52). Owner only, like the rest of that navigation.
  const totalsQuery = useQuery({
    queryKey: ['fitimet-totalet'],
    queryFn: () => apiGet<HeaderTotals>('/fitimet/totalet'),
    enabled: user?.role === ROLES.OWNER,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/fitimet/${biznesi}/rreshtat/${id}`),
    onSuccess: () => {
      toast.success('Rreshti u fshi me sukses.');
      void queryClient.invalidateQueries({ queryKey: ['fitimet'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  async function download(path: string, params?: Record<string, unknown>) {
    try {
      await apiDownload(path, params);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Shkarkimi dështoi.');
    }
  }

  if (!business) {
    return <div className="rounded-xl bg-white p-6 text-red-700">Biznesi nuk u gjet.</div>;
  }

  if (!hasRevenue) {
    return (
      <div className="rounded-xl bg-white p-8 text-center">
        <h1 className="text-lg font-semibold">Fitimet — Stargroup</h1>
        <p className="mt-2 text-sm text-muted">
          Stargroup nuk ka fitime të vetat. Fitimet e grupit janë shuma e tre bizneseve.
        </p>
        <Link to="/fitimet/krahasimi" className={`mt-4 inline-block ${btn.primary}`}>
          Shiko krahasimin
        </Link>
      </div>
    );
  }

  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;
  const dayTotals = new Map((listQuery.data?.dayTotals ?? []).map((d) => [d.data, d.total]));

  // Rows arrive sorted by date desc, so a day ends where the next date differs.
  const isLastOfDay = (index: number) =>
    index === rows.length - 1 || rows[index]!.data !== rows[index + 1]!.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Fitimet — {business.name}</h1>
          <p className="text-sm text-muted">Të ardhurat sipas ditës</p>
        </div>
        {user?.role === ROLES.OWNER && (
          <div className="flex gap-2">
            <Link to="/fitimet/krahasimi" className={btn.secondary}>
              Krahasimi
            </Link>
            <Link to="/fitimet/raportet" className={btn.secondary}>
              Raportet
            </Link>
          </div>
        )}
      </div>

      {user?.role === ROLES.OWNER && totalsQuery.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {totalsQuery.data.businesses.map((b) => (
            <div key={b.slug} className="rounded-xl bg-white p-4">
              <div className="text-xs font-medium text-muted">{b.name}</div>
              <div className="tnum mt-1 text-xl font-semibold">{formatMkd(b.total)}</div>
            </div>
          ))}
          <div className="rounded-xl bg-white p-4 ring-1 ring-brand-200">
            <div className="text-xs font-medium text-muted">Stargroup</div>
            <div className="tnum mt-1 text-xl font-semibold">
              {formatMkd(totalsQuery.data.grandTotal)}
            </div>
          </div>
        </div>
      )}

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko artikull"
      >
        <button
          type="button"
          onClick={() => download(`/fitimet/${biznesi}/eksport`, { search, startDate, endDate })}
          disabled={rows.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
        <button type="button" onClick={() => setEditing({ id: null })} className={btn.primary}>
          Shto fitim
        </button>
      </FilterBar>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Artikulli</th>
                <th className="px-4 py-3 font-semibold">Periudha</th>
                <th className="px-4 py-3 text-right font-semibold">Numri</th>
                <th className="px-4 py-3 text-right font-semibold">Shuma</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna për këtë periudhë.
                  </td>
                </tr>
              )}

              {rows.map((row, index) => (
                <Fragment key={row.ID}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-muted">{index + 1}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.data)}</td>
                    <td className="px-4 py-2.5">{row.artikulli ?? 'N/A'}</td>
                    <td className="px-4 py-2.5 text-muted">{row.periudha ?? ''}</td>
                    <td className="tnum px-4 py-2.5 text-right">
                      {row.numri == null ? '/' : formatNumber(row.numri)}
                    </td>
                    <td className="tnum px-4 py-2.5 text-right font-medium">
                      {formatMkd(row.cmimi)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        {Number(row.hasFile) === 1 && (
                          <button
                            type="button"
                            onClick={() => download(`/fitimet/rreshti/${row.ID}/fatura`)}
                            className="rounded-lg px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50"
                          >
                            Fatura
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditing({ id: row.ID })}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                        >
                          Ndrysho
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleting(row)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Fshij
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isLastOfDay(index) && (
                    <tr className="border-b border-slate-200 bg-emerald-50">
                      <td className="px-4 py-2 text-xs font-semibold text-emerald-900" colSpan={5}>
                        Totali i ditës {formatDate(row.data)}
                      </td>
                      <td className="tnum px-4 py-2 text-right font-semibold text-emerald-900">
                        {formatMkd(dayTotals.get(row.data) ?? 0)}
                      </td>
                      <td />
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-muted">
            Shuma totale nga {formatDate(startDate)} deri më {formatDate(endDate)}:
          </span>
          <span className="tnum text-lg font-semibold text-ink">{formatMkd(total)}</span>
        </div>
      </div>

      {editing && (
        <FitimModal
          biznesi={biznesi}
          businessName={business.name}
          rowId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Të dhënat u ruajtën me sukses!');
            void queryClient.invalidateQueries({ queryKey: ['fitimet'] });
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message="A jeni i sigurt që dëshironi të fshini këtë rresht?"
          confirmLabel="Fshij"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.ID);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
